import { Link, useRouterState } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'
import { PortalMenuItems } from '@/shared/components/PortalMenuItems'
import { useOptionalModuleNavFlags } from '@/shared/hooks/useOptionalModuleNavFlags'
import { defaultPortalNavigation, projectPortalNavigation } from '@/shared/lib/portalNavigation'
import { cn } from '@/shared/lib/utils'
import type { PortalNavigationItem } from '@/shared/schemas/brand.schema'

const linkBase =
  'inline-flex items-center gap-1 rounded-[var(--brand-radius-pill,999px)] px-4 py-2 text-sm font-semibold text-[var(--brand-text-primary)] no-underline outline-none ring-offset-background transition-colors hover:bg-[var(--brand-primary-soft)] hover:text-[var(--brand-primary-strong)] focus-visible:ring-2 focus-visible:ring-[var(--brand-ring)] aria-[current=page]:bg-[var(--brand-primary-soft)] aria-[current=page]:text-[var(--brand-primary-strong)]'

export function PortalNavLinks({
  variant,
  onNavigate,
  showLoginLink,
  headerConfigured,
}: {
  variant: 'header' | 'sheet'
  onNavigate?: () => void
  showLoginLink: boolean
  headerConfigured: PortalNavigationItem[] | null
}) {
  const { t } = useTranslation()
  const pathname = useRouterState({ select: (state) => state.location.pathname })
  const moduleFlags = useOptionalModuleNavFlags()
  const source = headerConfigured === null || headerConfigured.length === 0
    ? defaultPortalNavigation(t)
    : headerConfigured
  const items = projectPortalNavigation(source, moduleFlags)
  const navClass = variant === 'header' ? 'hidden items-center gap-1 lg:flex' : 'flex flex-col gap-1 px-4 pb-6'

  return (
    <nav
      id={variant === 'header' ? 'app-desktop-nav' : 'app-mobile-sheet-nav'}
      className={navClass}
      aria-label={t('app.layout.main_nav')}
    >
      <PortalMenuItems
        items={items}
        pathname={pathname}
        variant={variant}
        linkClassName={linkBase}
        onNavigate={onNavigate}
        srExternal={t('app.layout.external_link_sr')}
      />
      {showLoginLink ? (
        <Link
          to="/auth/login"
          className={cn(
            linkBase,
            'bg-[var(--brand-primary-strong)] text-[var(--brand-on-primary)] hover:bg-[var(--brand-primary-hover)] hover:text-[var(--brand-on-primary)] lg:ml-2',
          )}
          onClick={onNavigate}
        >
          {t('app.layout.nav_login')}
        </Link>
      ) : null}
    </nav>
  )
}
